<template>
  <MComment class="comment-add-box">
    <template v-slot:content>
      <MRow :gutter="0">
        <MCol class="box-container">
          <slot name="editor-actions" />
          <div class="flex">
            <MCol class="editor-row">
              <FlotoForm
                layout="vertical"
                class="flex flex-col items-end w-full"
                @submit="(e) => handleFormSubmit(e)"
              >
                <MRow
                  v-if="allowSubject || allowEmail || allowCcEmail"
                  class="w-full forward-details flex flex-col"
                  :gutter="0"
                >
                  <FlotoFormItem
                    v-if="allowEmail"
                    id="emails-picker"
                    class="w-full"
                    :rules="validationRules"
                    :validation-label="$tc('email')"
                  >
                    <FlotoEmailPicker
                      v-model="emailIds"
                      type="email"
                      @toggle-cc="handleToggleCc"
                    />
                  </FlotoFormItem>
                  <FlotoFormItem
                    v-if="allowCcEmail && showCcField"
                    id="emails-picker"
                    class="w-full"
                  >
                    <FlotoEmailPicker
                      v-model="ccEmailIds"
                      :cc-email="true"
                      type="email"
                    />
                  </FlotoFormItem>
                  <div v-if="allowSubject" class="flex items-center border-bot">
                    <span class="pl-2"> {{ $t('subject') }}: </span>
                    <FlotoFormItem
                      id="subject-input"
                      v-model="subject"
                      class="w-full"
                      :validation-label="$t('subject')"
                      :placeholder="$t('subject')"
                      rules="required"
                      :bordered="false"
                    >
                    </FlotoFormItem>
                  </div>
                  <div
                    v-if="
                      allowUserToSelectFromEmail &&
                      allowOutgoingEmail &&
                      moduleName === $constants.REQUEST
                    "
                  >
                    <FlotoFormItem
                      id="outgoing-mail-picker"
                      :validation-label="$tc('outgoing_email')"
                      rules="nonzero"
                      :bordered="false"
                    >
                      <div
                        class="flex items-center gap-2 outgoing-email-picker"
                      >
                        <small
                          class="text-neutral whitespace-nowrap"
                          style="min-width: 105px"
                        >
                          {{ $tc('outgoing_email') }} :
                        </small>
                        <OutgoingMailPicker
                          v-model="emailSendConfigId"
                          class="flex-1 min-w-0"
                          as-input
                          only-enabled-options
                        />
                      </div>
                    </FlotoFormItem>
                  </div>
                </MRow>
                <FlotoFormItem
                  id="text-input"
                  rules="required"
                  class="w-full"
                  :validation-label="$t('text')"
                >
                  <FlotoRichEditor
                    :key="renderCount"
                    ref="editorRef"
                    v-model="text"
                    :auto-focus="false"
                    :name="$t('text')"
                    :rows="richBoxRows"
                    :affix-toolbar="false"
                    :toolbar="conversationType.editorToolbar || false"
                    :enable-mention="conversationType.enableMention"
                    :placeholder="$t('start_typing')"
                    :min-rows="computedEditorMinRows"
                    :max-height="computedEditorMaxHeight"
                    class="flex-1 mb-1"
                  >
                    <template v-slot:attachments-list>
                      <div
                        :class="{
                          'border-top': attachments.length > 0,
                        }"
                        class="px-2 pb-2"
                      >
                        <FlotoAttachment v-model="attachments" only-list />
                      </div>
                    </template>
                    <template v-slot:editor-bar-actions>
                      <span class="flex items-center border-r">
                        <EditorButton title="Insert attachment" class="mr-1">
                          <FlotoAttachment
                            v-model="attachments"
                            :show-label="false"
                            :show-upload-list="false"
                            as-link
                            @file-list-updated="fileList = $event"
                          />
                        </EditorButton>
                        <!-- <EditorButton title="Insert canned response">
                          <MIcon class="text-neutral" name="comment-check" />
                          {{ $t('canned_response') }}
                        </EditorButton> -->
                        <InsertResponseTemplate
                          v-if="allowTemplates"
                          class="mr-1"
                          :resource="resource"
                          @selected="handleInsertTemplate"
                        />
                        <InsertKnowledge
                          v-if="allowKnowledges"
                          class="mr-1"
                          :resource="resource"
                          :size="'lg'"
                          @selected="handleInsertKnowledge"
                        />
                        <MCheckbox
                          v-if="
                            conversationType.type === 'note' &&
                            showNoteAsFirstResponse &&
                            !isPortalLogin &&
                            moduleName === $constants.REQUEST
                          "
                          id="consider-note-as-first-response-checkbox"
                          v-model="noteAsFirstResponse"
                          class="mx-2"
                        >
                          <span class="text-neutral-darker">{{
                            $tc('consider_note_as_first_response')
                          }}</span>
                        </MCheckbox>
                      </span>
                    </template>
                    <template v-slot:submit-button>
                      <MTooltip v-if="allowSaveInDraft">
                        <template v-slot:trigger>
                          <MButton
                            id="save-as-draft-btn"
                            :loading="processing"
                            class="mx-1"
                            shape="circle"
                            type="submit"
                            @click="submitType = 'draft'"
                          >
                            <MIcon
                              v-if="!processing"
                              name="save"
                              size="sm"
                              class="mx-1"
                            />
                            <span v-else />
                          </MButton>
                        </template>
                        {{ $tc('save_as_draft') }}
                      </MTooltip>
                      <MTooltip
                        v-if="
                          !knowledgeIsInserted &&
                          conversationType.type === 'solution'
                        "
                      >
                        <template v-slot:trigger>
                          <MButton
                            id="add-to-knowledge-btn"
                            :loading="processing"
                            class="mx-1"
                            shape="circle"
                            type="submit"
                            @click="submitType = 'knowledge'"
                          >
                            <MIcon
                              v-if="!processing"
                              name="lightbulb-on"
                              size="sm"
                              class="mx-1"
                            />
                            <span v-else />
                          </MButton>
                        </template>
                        {{ $tc('save_and_add_to_knowledge') }}
                      </MTooltip>
                      <MTooltip>
                        <template v-slot:trigger>
                          <MButton
                            id="save-btn"
                            :loading="processing"
                            class="mx-1"
                            shape="circle"
                            type="submit"
                            :style="{ zIndex: 2 }"
                            @click="submitType = 'publish'"
                          >
                            <MIcon
                              v-if="!processing"
                              name="paper-plane"
                              size="sm"
                              class="mx-1"
                            />
                            <span v-else />
                          </MButton>
                        </template>
                        {{
                          [
                            'technician_to_requester',
                            'forward_notification',
                            'requestor_to_technician',
                          ].indexOf(conversationType.type) >= 0
                            ? $tc('send')
                            : $tc('save')
                        }}
                      </MTooltip>
                    </template>
                  </FlotoRichEditor>
                </FlotoFormItem>
                <span slot="submit"> </span>
              </FlotoForm>
            </MCol>
          </div>
          <!-- <MCol class="mb-3 px-2">
            <FlotoAttachment v-model="attachments" only-list />
          </MCol> -->
          <!-- <MCol class="mb-3 px-2 flex">
            <FlotoAttachment
              v-model="attachments"
              :show-upload-list="false"
              as-link
              @file-list-updated="fileList = $event"
            />
            <a href="javascript:;" class="text-neutral mx-2 hidden">
              <MIcon name="comment-check" class="mx-1" />
              {{ $t('canned_response') }}
            </a>
            <InsertResponseTemplate
              v-if="allowTemplates"
              :resource="resource"
              @selected="handleInsertTemplate"
            />
            <InsertKnowledge
              v-if="allowKnowledges"
              :resource="resource"
              @selected="handleInsertKnowledge"
            />
            <MCheckbox
              v-if="
                conversationType.type === 'note' &&
                showNoteAsFirstResponse &&
                !isPortalLogin &&
                moduleName === $constants.REQUEST
              "
              id="consider-note-as-first-response-checkbox"
              v-model="noteAsFirstResponse"
              class="mx-2"
            >
              <span class="text-neutral">{{
                $tc('consider_note_as_first_response')
              }}</span>
            </MCheckbox>
          </MCol> -->
        </MCol>
      </MRow>
    </template>
  </MComment>
</template>

<script>
import Pick from 'lodash/pick'
import Bus from '@utils/emitter'
import { authComputed } from '@state/modules/auth'
import { PreferenceComputed } from '@state/modules/preference'
import InsertResponseTemplate from './insert-response-template'
import InsertKnowledge from './insert-knowledge'
import OutgoingMailPicker from '@components/data-picker/outgoing-mail-picker'
import EditorButton from '@components/editor/editor-button.vue'
export default {
  name: 'ConversationAddBox',
  components: {
    InsertResponseTemplate,
    InsertKnowledge,
    OutgoingMailPicker,
    EditorButton,
  },
  model: {
    event: 'change',
  },
  props: {
    isPrivate: { type: Boolean, default: false },
    conversationType: { type: Object, required: true },
    processing: { type: Boolean, default: false },
    id: { type: Number, default: undefined },
    moduleName: { type: String, default: undefined },
    value: {
      type: String,
      required: false,
      default: undefined,
    },
    fileAttachments: {
      type: Array,
      required: false,
      default: () => [],
    },
    kb: {
      type: Array,
      required: false,
      default: () => [],
    },
    allowTemplates: { type: Boolean, default: false },
    allowKnowledges: { type: Boolean, default: false },
    allowSubject: { type: Boolean, default: false },
    allowEmail: { type: Boolean, default: false },
    allowCcEmail: { type: Boolean, default: false },
    allowOutgoingEmail: { type: Boolean, default: false },
    richBoxRows: { type: Number, default: 5 },
    latestReplay: {
      type: Object,
      default() {
        return {}
      },
    },
    forwardDefalutContent: {
      type: Object,
      default() {
        return {}
      },
    },
    resource: { type: Object, required: true },
    showNoteAsFirstResponse: { type: Boolean, default: false },
    allowSaveInDraft: { type: Boolean, default: false },
    defaultEmailSendConfigId: { type: Number, default: 0 },
    formData: {
      type: Object,
      default() {
        return {}
      },
    },
    isDraft: { type: Boolean, default: false },
  },
  data() {
    return {
      renderCount: 1,
      text: this.value,
      attachments: this.fileAttachments,
      kbAttachments: this.kb,
      fileList: [],
      knowledgeIsInserted: false,
      noteAsFirstResponse: false,
      subject:
        this.formData.subject ||
        `[${this.resource.name}]: ${this.resource.subject}`,
      emailIds: this.formData.emailIds || [],
      ccEmailIds: this.formData.ccEmailIds || [],
      emailSendConfigId: this.defaultEmailSendConfigId || undefined,
      showCcField: false,
      submitType: 'publish',
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1360,
      viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 768,
    }
  },
  computed: {
    ...authComputed,
    ...Pick(PreferenceComputed, ['allowUserToSelectFromEmail']),
    validationRules() {
      let rules = {
        required: (this.conversationType || {}).name === 'Forward',
      }
      return rules
    },
    computedEditorMaxHeight() {
      const minW = 1360
      const maxW = 1900
      const minH = 60
      const maxH = 350
      const w = this.viewportWidth || maxW
      // width-based interpolation
      let h = 0
      if (w <= minW) h = minH
      else if (w >= maxW) h = maxH
      else {
        const t = (w - minW) / (maxW - minW)
        h = Math.round(minH + t * (maxH - minH))
      }
      // also respect viewport height so editor never takes too much vertical space
      const vhCap = Math.floor((this.viewportHeight || 600) * 0.45) // 45% of viewport height
      return Math.min(h, vhCap)
    },
    computedEditorMinRows() {
      const lineHeight = 22
      const rows = Math.floor(this.computedEditorMaxHeight / lineHeight)
      return Math.max(4, rows)
    },
  },
  created() {
    this.prePopulateContentForReplay()
    this.prePopulateContentForForward()
    this.emailSendConfigId = this.defaultEmailSendConfigId
    // if (
    //   this.conversationType.type === 'technician_to_requester' &&
    //   !this.text &&
    //   this.user.signature
    // ) {
    //   this.text = `<br />${this.user.signature}`
    // }
  },
  mounted() {
    if (
      [
        'technician_to_requester',
        'requestor_to_technician',
        'forward_notification',
      ].indexOf(this.conversationType.type) >= 0
    ) {
      // this.$refs.editorRef.focus('start')
    } else {
      this.$refs.editorRef.focus('start')
    }
    const handleSetFrowardContent = (content) => {
      this.prePopulateContentForForward(content)
      this.renderCount = this.renderCount + 1
      setTimeout(() => {
        this.$refs.editorRef.focus('start')
      }, 100)
    }
    const handleSetReplyContent = (content) => {
      this.prePopulateContentForReplay(content)
      this.renderCount = this.renderCount + 1
      setTimeout(() => {
        this.$refs.editorRef.focus('start')
      }, 100)
    }
    Bus.$on('app:conversation:forward:content', handleSetFrowardContent)
    Bus.$on('app:conversation:reply:content', handleSetReplyContent)
    this.__handleResize = () => {
      this.viewportWidth = window.innerWidth || this.viewportWidth
      this.viewportHeight = window.innerHeight || this.viewportHeight
    }
    window.addEventListener('resize', this.__handleResize, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.__handleResize)
  },
  methods: {
    handleToggleCc(show) {
      this.showCcField = show
    },
    handelSaveAndAddToKnowledge() {
      this.handleSubmit(true)
    },
    prepaireContent(type, content) {
      let text = ''
      if (type === 'technician_to_requester' && this.user.signature) {
        text = text.concat(`<p></p><p></p>${this.user.signature}<p><br /></p>`)
      }
      const replyContent = content || this.latestReplay
      if (Object.keys(replyContent || {}).length && replyContent.text) {
        if (!text) {
          text = '<p></p><p></p>'
        }
        text = text.concat(
          `<br /><b>From:</b> ${
            replyContent.owner
          } <br /><b>Date:</b> ${this.$options.filters.datetime(
            replyContent.createdAt
          )} <br /> <hr />${replyContent.text}`
        )
        if ((replyContent.attachments || []).length) {
          this.attachments = replyContent.attachments
        }
        if (type === 'technician_to_requester') {
          this.emailIds = replyContent.emailIds
          this.ccEmailIds = replyContent.ccEmailIds
          this.emailSendConfigId = replyContent.emailSendConfigId
        }
      }
      return text
    },
    prePopulateContentForReplay(content) {
      if (
        ['technician_to_requester', 'requestor_to_technician'].indexOf(
          this.conversationType.type
        ) >= 0 &&
        !this.isDraft
      ) {
        this.text = this.prepaireContent(this.conversationType.type, content)
      }
    },
    prePopulateContentForForward(content) {
      if (this.isDraft && this.text) {
        return
      }
      const forwardDefalutContent = content || this.forwardDefalutContent
      if (['forward_notification'].indexOf(this.conversationType.type) >= 0) {
        let text = ''
        if (this.user.signature) {
          text = text.concat(
            `<p></p><p></p>${this.user.signature}<p><br /></p>`
          )
        }
        if (Object.keys(forwardDefalutContent || {}).length) {
          if (!text) {
            text = '<p></p><p></p>'
          }
          text = text.concat(
            `<br /><b>From:</b> ${
              forwardDefalutContent.owner
            } <br /><b>Date:</b> ${this.$options.filters.datetime(
              forwardDefalutContent.createdAt
            )} <br /> <hr />${forwardDefalutContent.text}`
          )
        }
        this.text = text
        if ((forwardDefalutContent.attachments || []).length) {
          this.attachments = forwardDefalutContent.attachments
        }
        if (this.conversationType.type === 'technician_to_requester') {
          this.emailIds = forwardDefalutContent.emailIds
          this.ccEmailIds = forwardDefalutContent.ccEmailIds
          this.emailSendConfigId = forwardDefalutContent.emailSendConfigId
        }
      }
    },
    handelSaveInDraft() {
      this.handleSubmit(false, true)
    },
    handleSubmit(saveAndAddToKnowledge = false, isDraft = false) {
      if (this.processing) {
        return
      }
      const eventName =
        ['solution', 'conclusion', 'diagnosis'].indexOf(
          this.conversationType.type
        ) >= 0
          ? 'update'
          : 'submit'
      this.$emit(
        eventName,
        {
          id: this.id,
          text: this.text,
          fileAttachments: this.attachments,
          kbAttachments: this.kbAttachments,
          name: this.conversationType.type,
          noteAsFirstResponse: this.noteAsFirstResponse,
          subject: this.subject,
          emailIds: this.emailIds,
          ccEmailIds: this.ccEmailIds,
          emailSendConfigId: this.emailSendConfigId,
          conversationState: isDraft ? 'draft' : 'published',
        },
        saveAndAddToKnowledge
      )
    },
    reset() {
      this.text = this.value
      this.attachments = this.fileAttachments || []
      this.kbAttachments = this.kb
    },
    handleInsertTemplate(template) {
      this.text = template.content
      this.attachments = template.fileAttachments || []
      this.renderCount = this.renderCount + 1
    },
    insertKnowledgeContent(data) {
      this.$refs.editorRef.insertHTML(data)
    },
    handleInsertKnowledge(kb, link) {
      if (link) {
        const url = window.location.href
        let domain = new URL(url)
        const link = `<a href="${domain.origin}/support-portal/knowledge/${kb.id}" target="_blank" >
                  ${domain.origin}/support-portal/knowledge/${kb.id}
                </a><br />`
        this.insertKnowledgeContent(link)
      } else {
        this.insertKnowledgeContent(kb.content)
        this.attachments = kb.fileAttachments || []
      }
      this.renderCount = this.renderCount + 1
      this.knowledgeIsInserted = true
    },
    handleFormSubmit(e) {
      e.preventDefault()

      if (this.processing) {
        return
      }

      switch (this.submitType) {
        case 'draft':
          this.handleSubmit(false, true)
          break
        case 'knowledge':
          this.handleSubmit(true)
          break
        case 'publish':
          this.handleSubmit()
          break
      }
    },
  },
}
</script>

<style lang="less" scoped>
.box-container {
  @apply border-solid border rounded px-6;

  border-color: var(--border-color);

  .@{ant-prefix}-form-vertical .@{ant-prefix}-form-item {
    padding-bottom: 0 !important;
    margin-bottom: 0 !important;
  }
}

.border-r {
  padding: 0 10px;
  border-width: 2px;
  border-right: 1px solid var(--border-color);
}
</style>
<style lang="less">
.forward-details {
  // border-bottom: 1px solid var(--border-color);

  .ant-btn.ant-btn-primary.ant-btn-circle {
    position: unset !important;
    right: unset !important;
    bottom: unset !important;
    width: unset !important;
    min-width: unset !important;
    height: unset !important;
  }
}

.outgoing-email-picker {
  padding: 2px 0 2px 8px;
  border-bottom: 1px solid var(--border-color);
  .@{ant-prefix}-input {
    padding-left: 0 !important;
    border: none !important;
  }
}
</style>
